import React from 'react';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './QuestionContent.scss';

const difficultyMap = {
  EZ: 'Easy',
  MD: 'Medium',
  HD: 'Hard',
  UN: 'Unknown',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

const DIFFICULTY_COLORS = {
  easy: 'green',
  medium: 'orange',
  hard: 'red',
  unknown: 'default',
};

const getDifficultyColor = difficulty => {
  if (!difficulty) return 'default';
  const difficultyText = difficultyMap[difficulty] || difficulty;
  const normalizedDifficulty = difficultyText.toString().toLowerCase();
  return DIFFICULTY_COLORS[normalizedDifficulty] || 'default';
};

const CodingQuestion = ({ curr_question }) => {
  const { t } = useTranslation('practice_tests');
  let count = 0;
  let description =
    curr_question.description &&
    curr_question.description.split('\n').map(function (line) {
      if (
        line.includes('Input format') ||
        line.includes('Output format') ||
        line.includes('Constraints')
      ) {
        return (
          <div
            key={count++}
            className={styles['desc-style']}
          >
            {line}
          </div>
        );
      } else if (
        line.includes('Input:') ||
        line.includes('Output:') ||
        line.includes('Explanation:')
      ) {
        return (
          <div
            key={count++}
            className={styles['desc-style']}
          >
            {line}
          </div>
        );
      } else {
        return (
          <div
            key={count++}
            className={styles['contentstyle']}
          >
            {line}
          </div>
        );
      }
    });

  return (
    <div
      style={{
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        background: '#ffffff',
        marginBottom: '20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          marginBottom: '20px',
        }}
        data-test-id="coding-question-metadata"
        data-specific-id="coding-question-header"
      >
        <div
          data-test-id="points-display"
          data-specific-id="coding-points"
        >
          {t('practice_tests.test_result.points', { points: curr_question.points })}
        </div>
        <div
          data-test-id="difficulty-container"
          data-specific-id="coding-difficulty"
        >
          <Tag
            color={getDifficultyColor(curr_question.difficulty)}
            style={{ padding: '4px 8px' }}
            data-test-id="difficulty-tag"
            data-specific-id="coding-difficulty-tag"
          >
            {difficultyMap[curr_question.difficulty] || curr_question.difficulty || 'Medium'}
          </Tag>
        </div>
      </div>

      {/* Question Title */}
      <h1
        style={{ margin: 0, fontWeight: 700, fontSize: '20px', marginBottom: '20px' }}
        data-test-id="coding-question-title"
        data-specific-id="coding-title"
      >
        {curr_question.title}
      </h1>

      {(curr_question.tags || curr_question.tag_ids) && (
        <div
          style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}
          data-test-id="coding-tags"
          data-specific-id="coding-question-tags"
        >
          {curr_question.tags && curr_question.tags.length > 0
            ? curr_question.tags.map((tag, index) => {
                const tagName = typeof tag === 'object' ? tag.name : tag;
                return (
                  <Tag
                    key={tag.id || index}
                    style={{
                      borderRadius: '4px',
                      padding: '4px 8px',
                      color: 'rgba(24, 144, 255, 1)',
                      border: '1px solid rgba(145, 213, 255, 1)',
                      background: '#E6F7FF',
                    }}
                    data-test-id={`coding-tag-${index}`}
                    data-specific-id={`coding-tag-${tag.id || tagName}`}
                  >
                    {tagName}
                  </Tag>
                );
              })
            : curr_question.tag_ids && curr_question.tag_ids.length > 0
            ? curr_question.tag_ids.map((tagId, index) => (
                <Tag
                  key={tagId || index}
                  style={{
                    borderRadius: '4px',
                    padding: '4px 8px',
                    color: 'rgba(24, 144, 255, 1)',
                    border: '1px solid rgba(145, 213, 255, 1)',
                    background: '#E6F7FF',
                  }}
                  data-test-id={`coding-tag-id-${index}`}
                  data-specific-id={`coding-tag-id-${tagId}`}
                >
                  {tagId}
                </Tag>
              ))
            : null}
        </div>
      )}

      {/* Question Description */}
      <div
        style={{
          fontSize: '14px',
          color: '#374151',
          lineHeight: '1.8',
          marginBottom: '20px',
        }}
        data-test-id="coding-description"
        data-specific-id="coding-question-description"
      >
        {description}
      </div>
    </div>
  );
};

export default CodingQuestion;
